import {
  GlobalScore,
  Model,
  PDFReportType,
  Project,
  Result,
  Subtitle,
  Title,
  Value,
} from '@gameonly/core';

import Eolien from '../pdf/images/eolien.webp';
import Lake from '../pdf/images/lake.webp';
import Mountain from '../pdf/images/mountain.webp';
import Smoke from '../pdf/images/smoke.webp';
import { flattenParameters } from '../project/utils';

const BACKGROUNDS: Record<string, string> = {
  co2_title: Smoke,
  ressources_title: Mountain,
  energy_title: Eolien,
  eau_title: Lake,
  default: Mountain,
};

export const getCategoryBackground = (results: Result[]) => {
  const titleResult = results.find((result) => result.type === 'title');
  if (!titleResult) {
    return BACKGROUNDS.default;
  }
  return BACKGROUNDS[titleResult.code as string] || BACKGROUNDS.default;
};

export enum PageType {
  COVER_COMPLETE = 'COVER_COMPLETE',
  COVER_SIMPLE = 'COVER_SIMPLE',
  SUMMARY = 'SUMMARY',
  MAIN_INDICATOR = 'MAIN_INDICATOR',
  RESULT_FULL_PAGE = 'RESULT_FULL_PAGE',
  RESULTS_DETAILED = 'RESULTS_DETAILED',
  BACK_COVER = 'BACK_COVER',
}

export const getTitleResult = (results: Result[] | undefined): Title | null => {
  return results?.find((r) => r.type === 'title') as Title | null;
};

export const getGlobalScore = (
  results: Result[] | undefined,
): GlobalScore | null => {
  return results?.find((r) => r.type === 'globalScore') as GlobalScore | null;
};

export const getSubtitleResult = (
  results: Result[] | undefined,
): Subtitle | null => {
  return results?.find(
    (result) => result.type === 'subtitle',
  ) as Subtitle | null;
};

type CodeChecker = (code: string | undefined | null) => boolean;

type ReportSimpleCodesToFindType = {
  site: (string | CodeChecker)[];
  game: (string | CodeChecker)[];
};

const ReportSimpleCodesToFind: ReportSimpleCodesToFindType = {
  site: [
    (code) => !!code?.includes('co2_detailed'),
    'beges_categories_v2',
    'ghg_categories',
  ],
  game: [
    'co2_detailed_treemap',
    'beges_categories',
    'ghg_categories_displayed',
  ],
};

export type DetailedPageNumberType = {
  currentPage: number;
  totalPage: number;
};

export type PaginatedResultData = {
  projectName?: string;
};

export type PaginatedResult = {
  pageType: PageType;
  pageNumber?: number;
  detailedPageNumber?: DetailedPageNumberType;
  data?: PaginatedResultData;
  results?: Result[];
};

export const resultHasData = (result: Result) => {
  switch (result.type) {
    case 'scoreCard':
    case 'cncExport':
    case 'indicator':
    case 'nav':
    case 'toggle':
    case 'title':
    case 'globalScore':
    case 'text':
    case 'subtitle':
    case 'indicator2Values':
    case 'treemap':
      return true;
    case 'pie1D':
      return result.data && result.data.length > 0;
    case 'barStacked2D':
      return result.data && result.data.barData.length > 0;
    case 'barStacked1D':
    case 'barSingle1D':
      return (
        result.data &&
        result.data.data.some((entry) =>
          Object.values(entry).some((value) => value > 0),
        )
      );
    case 'bar1D':
      return result.data && result.data.length > 0;
    case 'table':
      return result.table && result.table.some((row) => row.length > 0);
  }
};

export const getPaginatedResultsByCategories = (
  results: Result[],
  model: Model,
  reportType: PDFReportType,
  projectName: string,
) => {
  let paginatedResults: PaginatedResult[] = [];

  if (reportType === PDFReportType.Complete) {
    if (model.type === 'site')
      paginatedResults = getPaginatedResultsForSiteComplete(results);
    if (model.type === 'game')
      paginatedResults = getPaginatedResultsForGameComplete(results);
    return [
      {
        pageType: PageType.COVER_COMPLETE,
        data: {
          projectName,
        },
      },
      {
        pageType: PageType.SUMMARY,
      },
      ...paginatedResults,
      {
        pageType: PageType.BACK_COVER,
      },
    ];
  }

  if (reportType === PDFReportType.Simple) {
    return [
      {
        pageType: PageType.COVER_SIMPLE,
        data: {
          projectName,
        },
      },
      ...getPaginatedResultsForSimple(results, model.type),
      {
        pageType: PageType.BACK_COVER,
      },
    ];
  }

  return null;
};

const getPaginatedResultsForSimple = (
  results: Result[],
  modelType: string,
): PaginatedResult[] => {
  if (modelType !== 'site' && modelType !== 'game') return [];

  const codesToFind = ReportSimpleCodesToFind[modelType];

  return codesToFind
    .map((matcher) =>
      typeof matcher === 'function'
        ? results.find((r) => matcher(r.code))
        : results.find((r) => r.code === matcher),
    )
    .filter((r): r is Result => Boolean(r))
    .map((result) => ({
      pageType: PageType.RESULT_FULL_PAGE,
      results: [result],
    }));
};

const getPaginatedResultsForSiteComplete = (results: Result[]) => {
  let paginatedResults: PaginatedResult[] = [];
  for (let i = 0; i < results.length; ) {
    const result = results[i];

    // Case 1. If the result is a title, we display the next 3 results on the same page (usually it's title, subtitle, and GlobalScore.)
    if (result.type === 'title' && !result.code?.includes('beges')) {
      const titleGroup = results.slice(i, i + 3);
      paginatedResults.push({
        pageType: PageType.MAIN_INDICATOR,
        results: titleGroup,
      });
      i += 3;
      continue;
    }

    // Case 2. If the result is a subtitle, we paginate the next results by group of 5 (1 title + 4 graphs)
    if (result.type === 'subtitle') {
      const { paginatedResults: subPages, consumed } = paginateSubGroup(
        results,
        i,
      );
      paginatedResults.push(...subPages);
      i += consumed;
      continue;
    }

    // Case 3. If the result is a text or a detailed info, we display it on a single page
    if (
      result.code?.includes('detailed') ||
      result.code?.includes('beges_categories') ||
      result.code?.includes('ghg_categories_displayed') ||
      result.type === 'text'
    ) {
      paginatedResults.push({
        pageType: PageType.RESULT_FULL_PAGE,
        results: [result],
      });
      i += 1;
      continue;
    }

    i += 1;
  }
  return paginatedResults;
};

const getPaginatedResultsForGameComplete = (results: Result[]) => {
  let paginatedResults: PaginatedResult[] = [];

  let k = 0;

  // Case 1. If the result is a title : we pick the title and the detailed graphs
  for (let i = 0; i < results.length; ) {
    k += 1;

    if (k > 50) break;

    const result = results[i];

    if (result.type === 'title' && !result.code?.includes('beges')) {
      const titleGroup = results.slice(i, i + 3);
      paginatedResults.push({
        pageType: PageType.MAIN_INDICATOR,
        results: titleGroup,
      });

      const nextResults = results.slice(i + 2); // to avoid subtitle just after
      const nextTitleIndex = nextResults.findIndex(
        (r) => r.type.includes('title') || r.type.includes('subtitle'),
      );

      const fullPageTypes = ['treemap', 'bar1D'];

      for (const type of fullPageTypes) {
        const typeIndex = nextResults.findIndex((r) => r.type === type);
        if (typeIndex > -1 && typeIndex < nextTitleIndex) {
          paginatedResults.push({
            pageType: PageType.RESULT_FULL_PAGE,
            results: [nextResults[typeIndex]],
          });
        }
      }
      i += nextTitleIndex;
      continue;
    }

    // Case 2
    if (result.type === 'subtitle') {
      const { paginatedResults: subPages, consumed } = paginateSubGroup(
        results,
        i,
      );
      paginatedResults.push(...subPages);
      i += consumed;
      continue;
    }

    // Case 3
    if (
      result.code?.includes('beges_categories') ||
      result.code?.includes('ghg_categories_displayed') ||
      result.type === 'text'
    ) {
      paginatedResults.push({
        pageType: PageType.RESULT_FULL_PAGE,
        results: [result],
      });
      i += 1;
      continue;
    }

    i += 1;
  }
  return paginatedResults;
};

const paginateSubGroup = (
  results: Result[],
  startIndex: number,
  graphByPage: number = 4,
) => {
  const paginatedResults: { pageType: PageType; results: Result[] }[] = [];

  const remainingResults = results.slice(startIndex);
  const searchStart = remainingResults.slice(1);
  const relativeIndex = searchStart.findIndex(
    (r) =>
      r.type.includes('title') ||
      r.type.includes('subtitle') ||
      r.type.includes('text'),
  );
  const endOfSubGroup =
    relativeIndex !== -1 ? relativeIndex + 1 : remainingResults.length;
  let subGroup = remainingResults.splice(0, endOfSubGroup);

  let j = 0;
  const subGroupSubTitle = subGroup.find((r) => r.type === 'subtitle');
  subGroup = subGroup.filter((r) => r.type !== 'subtitle');
  while (subGroupSubTitle && j < subGroup.length) {
    paginatedResults.push({
      pageType: PageType.RESULTS_DETAILED,
      results: [subGroupSubTitle, ...subGroup.slice(j, j + graphByPage)],
    });

    j += graphByPage;
  }

  return { paginatedResults, consumed: endOfSubGroup };
};

const hasSameSubtitle = (a: PaginatedResult, b: PaginatedResult): boolean => {
  const subtitleResultA = a.results?.find(
    (result) => result.type === 'subtitle',
  ) as Subtitle;
  const subtitleResultB = b.results?.find(
    (result) => result.type === 'subtitle',
  ) as Subtitle;
  if (
    subtitleResultA?.text?.toLocaleUpperCase() ===
    subtitleResultB?.text?.toLocaleUpperCase()
  )
    return true;
  return false;
};

export const processPaginatedResults = (
  paginatedResults: PaginatedResult[],
): PaginatedResult[] => {
  // Assign page numbers
  paginatedResults.forEach((item, index) => {
    item.pageNumber = index + 1;
  });

  // Process detailedPageNumber for RESULTS_DETAILED
  let i = 0;
  while (i < paginatedResults.length) {
    const current = paginatedResults[i];

    if (current.pageType === PageType.RESULTS_DETAILED) {
      // Gather all consecutive RESULTS_DETAILED elements
      const group: PaginatedResult[] = [];
      let j = i;
      while (
        j < paginatedResults.length &&
        paginatedResults[j].pageType === PageType.RESULTS_DETAILED
      ) {
        group.push(paginatedResults[j]);
        j++;
      }

      // Process group members
      let k = 0;
      while (k < group.length) {
        const reference = group[k];
        const sameSubtitleGroup = [reference];
        let l = k + 1;
        while (l < group.length && hasSameSubtitle(group[l], reference)) {
          sameSubtitleGroup.push(group[l]);
          l++;
        }

        if (sameSubtitleGroup.length > 1) {
          sameSubtitleGroup.forEach((item, index) => {
            item.detailedPageNumber = {
              currentPage: index + 1,
              totalPage: sameSubtitleGroup.length,
            };
          });
        }

        k = l; // Move to the next non-processed item in the group
      }

      i = j; // Skip the entire processed group
    } else {
      i++;
    }
  }

  return paginatedResults;
};

export const getProjectYear = (project: Project): number | undefined => {
  const flattenParams = flattenParameters(project.sectors || []);
  let yearParameter;
  yearParameter = flattenParams.find(
    (parameter) =>
      parameter.id?.toLocaleLowerCase().includes('annee') ||
      parameter.id?.toLocaleLowerCase().includes('année'),
  );
  const year =
    yearParameter && Array.isArray(yearParameter.value)
      ? yearParameter?.value[0]
      : undefined;
  return year ? transformToNumber(year) : undefined;
};

export const transformToNumber = (value: Value): number | undefined => {
  if (typeof value === 'number') {
    return value;
  } else if (typeof value === 'string') {
    const parsedNumber = parseFloat(value);
    if (!isNaN(parsedNumber)) {
      return parsedNumber;
    }
  }
  return undefined;
};
